import { ApprovalStatus } from '@/entities/approvals/constants';

type objectMemberTypes = string | null | number;

export type LegacyPhotoValidation = {
  result: boolean;
  confidence: number;
  description: string;
};

export type LegacyPhoto = {
  [key: string]: objectMemberTypes | LegacyPhotoValidation | LegacyParams | null | undefined;
  image?: string | null;
  image_id?: string | null;
  index?: number;
  image_description?: string | null;
  validation?: LegacyPhotoValidation | null;
  params?: LegacyParams | null;
};

export type LegacyFile = Record<string, objectMemberTypes> & {
  file_id?: string | null;
  file_name?: string | null;
};

export interface LegacyRoccSeverity {
  category: number | null;
  threshold: number;
}

export interface LegacyParams {
  collapsed?: boolean;
  rocc_enabled?: boolean;
  severities?: Array<LegacyRoccSeverity>;
  header?: boolean;
  automatic?: boolean;
  multiple_selection?: boolean;
  response_set?: string;
  image?: string | null;
  image_id?: string | null;
  allow_drawing?: boolean;
  allow_gallery_selection?: boolean;
  allow_multiple?: boolean;
  date?: boolean;
  time?: boolean;
  rte_title?: boolean;
  show_rte_response?: boolean;
  ai_image_description_enabled?: boolean | null;
  ai_image_validation_enabled?: boolean | null;
  ai_image_validation_criteria?: string | null;
}

export interface LegacyFormParamResponse {
  uuid: string;
  title: string;
  title_translated?: string|null;
  score: number;
  negative: boolean;
  color: string;
}

export interface LegacyFormParams extends LegacyParams {
  response_sets: Array<{
    uuid: string;
    type?: string;
    responses: Array<LegacyFormParamResponse>;
  }>;
}

export type LegacyNegativeAnswerItem = Omit<LegacyItem, 'max_score' | 'score' | 'photos' | 'notes' | 'issues'>;

export type LegacyItemResponseType = LegacyPhoto | Array<LegacyPhoto> | Array<LegacyFile> | Array<string> | boolean
| string;

export type LegacyRoccResults = Array<{
  category?: number;
  count?: number;
  passing?: boolean;
}>;

export interface LegacyItem {
  allConditionsMet?: boolean;
  image_id?: string;
  image?: string;
  items?: Array<LegacyItem>;
  negative?: boolean;
  params: LegacyParams;
  percentage?: number | null;
  photos?: Array<LegacyPhoto>;
  responded: boolean;
  response?: LegacyItemResponseType;
  html_response?: string;
  rte_response?: boolean;
  response_type: string;
  status: string;
  title?: string;
  title_translated?: string|null;
  html_title?: string;
  html_title_translated?: string|null;
  type: string;
  uuid: string;
  index?: number;
  max_score?: number;
  score?: number;
  notes?: string;
  issues?: Array<LegacyIssue>;
  repeat?: boolean;
  assets?: Assets;
  rocc_results?: LegacyRoccResults;
}

interface Assets {
  documents: AssetOptions;
  images: AssetOptions;
  videos: AssetOptions;
  audios: AssetOptions;
}

type AssetOptions = AssetOption[];

interface AssetOption {
  id: string;
  name: string;
}

export interface LegacyForm {
  uuid: string;
  type: string;
  items: Array<LegacyItem>;
  params: LegacyFormParams;
  max_score?: number;
  score?: number;
  rocc_enabled?: boolean;
  rocc_passing?: boolean;
  rocc_results?: LegacyRoccResults;
}

export interface LegacyTemplate {
  display_score?: boolean;
  display_rocc_score?: boolean;
  display_description?: boolean;
  display_issues?: boolean;
  display_score_range_labels?: boolean;
  average_weighted_score_calculation?: boolean;
  display_score_result_of_pages?: boolean;
  display_entity_item?: boolean;
  display_negative_answers?: boolean;
  logo?: string | null;
  score_range_labels?: Array<{ min: number; max: number; color: string; label: string }>;
  exclude_questions_type_instruction?: boolean;
  exclude_questions_not_responded?: boolean;
}

export interface LegacyBreadcrumb {
  parent: { title: string };
  response?: Array<string>;
  title: string;
  type: string;
  uuid: number | string;
}

export type LegacyIssue = Record<string, objectMemberTypes> & {
  payload?: {
    item_uuid: string;
    breadcrumb: Array<LegacyBreadcrumb>;
  };
  priority: number;
  status: number;
};

export interface LegacyInspection {
  title: string | null;
  checklist_title: string | null;
  checklist_description: string | null;
  checklist_title_translated?: string|null;
  checklist_description_translated?: string|null;
  entity_type_of_response_migrated: undefined | boolean;
  conducted_at: number;
  conducted_by: string;
  inspection_id: number;
  pdf_url: string | null;
  photos_url: string | null;
  activities_count: number;
  form: LegacyForm;
  entity_item: number;
  entity_items: Record<number, string>;
  template: LegacyTemplate;
  issues: Array<LegacyIssue>;
  approval?: {
    id: number;
    status: ApprovalStatus;
    approver: null | {
      id: number;
      name: string;
      data: {
        name: string;
      };
    };
    updated_at: number;
  };
  percentage?: number;
  max_score?: number;
  score?: number;
}

export const IssuePriority = {
  Medium: 10,
  High: 20,
};

export const IssueStatus = {
  Open: 0,
  Solved: 10,
  InProgress: 20,
  CantDo: 30,
};
